/**
 * NARRATORIA HOUSE BRAND — shared tokens.
 *
 * The ink / parchment / antique-gold palette from narratoria-brand-spec.md,
 * expressed as class strings so a reskin is a change of tokens rather than a
 * rewrite of every component.
 *
 * Used by the toolkit front door, the hub and Marketing Compass. The Roadmap
 * and Story Bible predate this and carry their own equivalent styling inline;
 * the values here are taken from them, so the whole toolkit reads as one brand
 * from the Squarespace homepage through to each tool.
 *
 * From the spec, verbatim:
 *   ink        #14110d (Tailwind `ink`) · ink-2 #0e0c09 · ink-800 #1c1710
 *   parchment  #f5edd8 · parchment.tint #faf6ea · paper-line #e2d6b4
 *   gold       #d4af37  ← THE interactive accent (the role teal was playing)
 *   gold-deep  #9c7a1e  ← gold TEXT on cream, for contrast
 *   gold-soft  rgba(212,175,55,0.28) ← borders, chip outlines
 *
 * Display type is Cinzel, body is Cormorant Garamond, both already loaded by
 * index.html for the Roadmap. Uppercase micro-labels are 11-12px with
 * 0.12-0.18em tracking, per the spec.
 *
 * ⚠️ Contrast rule that is easy to get wrong: `text-gold` (#d4af37) is for INK
 * surfaces. On parchment it fails legibility — use `text-gold-deep` (#9c7a1e).
 */

/* ── Page ─────────────────────────────────────────────────── */
export const PAGE = 'min-h-screen bg-ink';
export const SHELL = 'mx-auto w-full max-w-3xl px-4 py-8';

/* Body copy is Cormorant Garamond; applied at the page root so every child
   inherits it without each component restating the family. */
export const BODY_FONT = "font-cormorant";

/* ── Panels ───────────────────────────────────────────────── */
// The main content surface: parchment on ink, as the Roadmap uses.
export const PANEL = 'rounded-2xl border border-gold-soft bg-parchment shadow-2xl';
export const PANEL_INNER = 'p-5 sm:p-6';
// A quieter panel for secondary blocks (skipped, assumptions).
export const PANEL_MUTED = 'rounded-xl border border-[#e2d6b4] bg-parchment-tint';
// Dark band, for the header lockup.
export const BAND = 'border-b border-gold-soft bg-ink-2';

/* ── Type ─────────────────────────────────────────────────── */
export const H1 = 'font-cinzel text-2xl font-bold tracking-wide text-parchment';
export const H1_ON_PARCHMENT = 'font-cinzel text-xl font-bold tracking-wide text-[#241f17]';
export const H2 = 'font-cinzel text-sm font-bold uppercase tracking-[0.14em] text-gold-deep';
export const H3 = 'font-cinzel text-base font-bold text-[#241f17]';
export const BODY = 'text-[#241f17]';
export const BODY_MUTED = 'text-[#4a4133]';
export const BODY_FAINT = 'text-[#6d6151]';
export const ON_INK = 'text-parchment/80';
export const MICRO = 'font-cinzel text-[11px] font-semibold uppercase tracking-[0.16em]';

/* ── Buttons ──────────────────────────────────────────────── */
// Gold CTA gradient with ink text, per the spec's shape conventions.
export const BTN_PRIMARY =
  'inline-flex items-center justify-center gap-2 rounded-xl border border-gold/50 '
  + 'bg-gradient-to-br from-[#d4af37] to-[#b8912b] px-4 py-2.5 font-cinzel text-xs '
  + 'font-bold uppercase tracking-[0.12em] text-[#1c1710] transition-all '
  + 'hover:-translate-y-px hover:brightness-110 disabled:opacity-50 disabled:hover:translate-y-0';
// Secondary on parchment.
export const BTN_SECONDARY =
  'inline-flex items-center justify-center gap-2 rounded-xl border border-[#c9b98f] '
  + 'bg-transparent px-4 py-2.5 font-cinzel text-xs font-bold uppercase '
  + 'tracking-[0.12em] text-[#4a4133] transition-colors hover:border-gold '
  + 'hover:text-gold-deep disabled:opacity-50';
// Ghost on ink (header controls).
export const BTN_ON_INK =
  'inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-white/5 px-3 py-1 '
  + 'font-cinzel text-[11px] font-semibold uppercase tracking-[0.14em] text-parchment/60 '
  + 'transition-colors hover:border-white/20 hover:text-parchment';

/* ── Quiz option rows ─────────────────────────────────────── */
export const OPTION =
  'flex w-full items-center justify-between gap-3 rounded-xl border px-4 py-3 text-left '
  + 'transition-colors';
export const OPTION_IDLE =
  'border-[#e2d6b4] bg-parchment-tint text-[#241f17] hover:border-gold/50 hover:bg-[#f2e8cf]';
export const OPTION_ACTIVE =
  'border-gold bg-gold/15 font-semibold text-[#241f17] shadow-sm';

/* ── Chips ────────────────────────────────────────────────── */
export const CHIP = 'inline-flex items-center gap-1 rounded-full border px-2.5 py-0.5 text-[11px] font-semibold';
export const CHIP_GOLD = 'border-gold-soft bg-gold/12 text-gold-deep';
export const CHIP_MUTED = 'border-[#d9cba6] bg-parchment-tint text-[#6d6151]';
// Status colours from the spec — muted, for use on cream.
export const CHIP_OK = 'border-[#2f5a34]/30 bg-[#2f5a34]/10 text-[#2f5a34]';
export const CHIP_WARN = 'border-[#8a3a1e]/30 bg-[#8a3a1e]/10 text-[#8a3a1e]';

/* ── Rules & progress ─────────────────────────────────────── */
export const HAIRLINE = 'border-[#e2d6b4]';
export const PROGRESS_TRACK = 'h-1.5 w-full overflow-hidden rounded-full bg-parchment/15';
export const PROGRESS_BAR = 'h-full rounded-full bg-gradient-to-r from-[#d4af37] to-[#e3c766] transition-all';

/* ── Inputs ───────────────────────────────────────────────── */
export const INPUT =
  'mt-1 w-full rounded-lg border border-[#d9cba6] bg-parchment-tint px-3 py-2 '
  + 'text-[#241f17] placeholder:text-[#9a8f78] focus:border-gold focus:outline-none';
